import React, { useState, useEffect } from 'react';
import { useQuizState } from '../hooks/useQuizState';
import { getAllQuestions, getSectionForQuestion } from '../data/questions';
import ProgressBar from './ProgressBar';
import LandingScreen from './LandingScreen';
import Question from './Question';
import EmailCapture from './EmailCapture';
import Results from './Results';

/**
 * Quiz Component
 * Main container that orchestrates the entire quiz flow
 * Manages state and renders appropriate screen based on current stage
 * Includes slide animations and auto-advance functionality
 */
export default function Quiz() {
  const {
    stage,
    currentQuestionIndex,
    answers,
    leadData,
    startQuiz,
    answerQuestion,
    nextQuestion,
    previousQuestion,
    submitLeadCapture,
    getProgress,
    getTimeToComplete,
    isCurrentQuestionAnswered,
  } = useQuizState();
  
  // Animation state
  const [animationState, setAnimationState] = useState('idle'); // 'idle', 'exiting', 'entering'
  const [animationDirection, setAnimationDirection] = useState('forward'); // 'forward', 'backward'
  const [landingExiting, setLandingExiting] = useState(false); // Track landing page exit animation
  const [captureExiting, setCaptureExiting] = useState(false); // Track capture page exit animation
  
  // Get all questions
  const allQuestions = getAllQuestions();
  const totalQuestions = allQuestions.length;
  
  // Get current question and its section
  const currentQuestion = allQuestions[currentQuestionIndex];
  const currentSection = currentQuestion ? getSectionForQuestion(currentQuestion.id) : null;
  
  // Handle start quiz with animation (Landing → First Question)
  const handleStartQuiz = () => {
    setLandingExiting(true);
    setAnimationDirection('forward');
    
    // After landing exit animation, start quiz and show first question entering
    setTimeout(() => {
      requestAnimationFrame(() => {
        startQuiz();
        setLandingExiting(false);
        setAnimationState('entering');
        
        // Reset to idle after enter animation
        setTimeout(() => {
          setAnimationState('idle');
        }, 400);
      });
    }, 400);
  };
  
  // Handle lead capture submission with animation (Capture → Results)
  const handleSubmitLeadCapture = (formData) => {
    setCaptureExiting(true);
    setAnimationDirection('forward');
    
    // After capture exit animation, submit and show results entering
    setTimeout(() => {
      requestAnimationFrame(() => {
        submitLeadCapture(formData);
        setCaptureExiting(false);
        setAnimationState('entering');
        
        // Reset to idle after enter animation
        setTimeout(() => {
          setAnimationState('idle');
        }, 400);
      });
    }, 400);
  };
  
  // Handle answer selection with auto-advance
  const handleAnswer = (answerId) => {
    if (currentQuestion) {
      // Select answer first (shows selection immediately)
      answerQuestion(currentQuestion.id, answerId);
      
      // Use requestAnimationFrame to ensure answer selection renders first,
      // then start animation in the next frame for clean separation
      requestAnimationFrame(() => {
        setAnimationDirection('forward');
        setAnimationState('exiting');
        
        // After exit animation completes, advance to next question
        setTimeout(() => {
          // Use requestAnimationFrame again for clean state transition
          requestAnimationFrame(() => {
            nextQuestion(totalQuestions);
            setAnimationState('entering');
            
            // Reset to idle after enter animation
            setTimeout(() => {
              setAnimationState('idle');
            }, 400);
          });
        }, 400);
      });
    }
  };
  
  // Handle next button (for navigation with animation)
  const handleNext = () => {
    setAnimationDirection('forward');
    setAnimationState('exiting');
    
    // After exit animation completes, change question
    setTimeout(() => {
      nextQuestion(totalQuestions);
      setAnimationState('entering');
      
      // Reset to idle after enter animation
      setTimeout(() => {
        setAnimationState('idle');
      }, 400);
    }, 400);
  };
  
  // Handle back button (for navigation with animation)
  const handleBack = () => {
    setAnimationDirection('backward');
    setAnimationState('exiting');
    
    // After exit animation completes, go back
    setTimeout(() => {
      previousQuestion();
      setAnimationState('entering');
      
      // Reset to idle after enter animation
      setTimeout(() => {
        setAnimationState('idle');
      }, 400);
    }, 400);
  };
  
  // Determine animation class based on state and direction
  const getAnimationClass = () => {
    if (animationState === 'exiting') {
      return animationDirection === 'forward' ? 'slide-exit-left' : 'slide-exit-right';
    }
    if (animationState === 'entering') {
      return animationDirection === 'forward' ? 'slide-enter-right' : 'slide-enter-left';
    }
    return '';
  };
  
  // Calculate progress percentage
  const progressPercentage = getProgress(totalQuestions);
  
  // Get current year for copyright
  const currentYear = new Date().getFullYear();
  
  // Render appropriate screen based on stage
  return (
    <>
      {/* Fixed Background Layer (shown for ALL stages) */}
      <div className="network-bg-fixed" />
      
      {/* Fixed Header with Logo and Progress Bar (shown for questions and capture stages) */}
      {(stage === 'question' || stage === 'capture') && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-navy-dark/95 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-2 sm:py-3">
            {/* Logo */}
            <div className="flex justify-center mb-2">
              <img 
                src="/logos/thryv_consulting_logo_02tw_web.png" 
                alt="Thryv Consulting" 
                className="h-6 sm:h-8"
              />
            </div>
            {/* Progress Bar */}
            <ProgressBar percentage={progressPercentage} />
          </div>
        </div>
      )}
      
      {/* Landing Screen */}
      {stage === 'landing' && (
        <div className={`overflow-hidden ${landingExiting ? 'slide-exit-left' : ''}`}>
          <LandingScreen onStart={handleStartQuiz} />
        </div>
      )}
      
      {/* Question Screen */}
      {stage === 'question' && currentQuestion && currentSection && (
        <div className="pt-20 sm:pt-24 overflow-hidden">
          <div key={currentQuestion.id} className={getAnimationClass()}>
            <Question
              question={currentQuestion}
              section={currentSection}
              selectedAnswer={answers[currentQuestion.id]}
              onAnswer={handleAnswer}
              onBack={handleBack}
              canGoBack={currentQuestionIndex > 0}
              isLastQuestion={currentQuestionIndex === totalQuestions - 1}
            />
          </div>
        </div>
      )}
      
      {/* Email Capture Screen */}
      {stage === 'capture' && (
        <div className="pt-20 sm:pt-24 overflow-hidden">
          <div key="email-capture" className={captureExiting ? 'slide-exit-left' : getAnimationClass()}>
            <EmailCapture 
              onSubmit={handleSubmitLeadCapture}
              onBack={handleBack}
              answers={answers}
              getTimeToComplete={getTimeToComplete}
            />
          </div>
        </div>
      )}
      
      {/* Results Screen */}
      {stage === 'results' && (
        <div className={`overflow-hidden ${animationState === 'entering' ? 'slide-enter-right' : ''}`}>
          <Results 
            answers={answers}
            leadData={leadData}
          />
        </div>
      )}
      
      {/* Fixed Copyright Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-navy-dark/80 backdrop-blur-sm py-2 text-center">
        <p className="text-gray-500 text-xs">
          © {currentYear} Thryv Consulting. All rights reserved.
        </p>
      </div>
    </>
  );
}

